
import { useCallback, useState, useEffect } from 'react';
import { UserProfile } from '@/types/assessment';
import { supabase } from '@/integrations/supabase/client';

export const useUserProfile = (userId?: string) => {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const fetchUserProfile = useCallback(async (profileId: string) => {
    try {
      // Use proper type handling with TypeScript
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', profileId)
        .maybeSingle();
        
      if (error) {
        console.error('Error fetching profile:', error);
        return null;
      }
      
      return data;
    } catch (error) {
      console.error('Error in fetchUserProfile:', error);
      return null;
    }
  }, []);
  
  const createUserProfile = useCallback(async (session: any) => {
    if (!session?.user) return null;
    
    try {
      const profileData = await fetchUserProfile(session.user.id);
        
      const userProfile: UserProfile = {
        id: session.user.id,
        name: profileData?.name || session.user.user_metadata?.name || 'User',
        email: session.user.email || '',
        phone: profileData?.phone || session.user.user_metadata?.phone || '',
        age: profileData?.age || session.user.user_metadata?.age || 21,
        educationStatus: (profileData?.education_status as any) || 
                        session.user.user_metadata?.education_status || 
                        'completed12th',
      };
      
      return userProfile;
    } catch (error) {
      console.error('Error creating user profile:', error);
      return null;
    }
  }, [fetchUserProfile]);

  // Load profile when userId is available
  useEffect(() => {
    if (!userId) return;
    
    const loadProfile = async () => {
      setLoading(true);
      try {
        const profileData = await fetchUserProfile(userId);
        
        if (profileData) {
          const userProfile: UserProfile = {
            id: userId,
            name: profileData.name || 'User',
            email: profileData.email || '',
            phone: profileData.phone || '',
            age: profileData.age || 21,
            educationStatus: (profileData.education_status as any) || 'completed12th',
          };
          setProfile(userProfile);
        }
      } catch (error) {
        console.error('Error loading profile:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadProfile();
  }, [userId, fetchUserProfile]);

  return {
    profile,
    loading,
    fetchUserProfile,
    createUserProfile
  };
};
